import type { Address } from '@medplum/fhirtypes'
import type { AddressValidationResult } from '@seen/request-types/addressLookup'
import { isEqual, mapValues } from 'lodash-es'
import { z } from 'zod'

export const addressFieldsSchema = z.object({
  line1: z.string(),
  line2: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
})

export type AddressFields = z.infer<typeof addressFieldsSchema>

// A text-only address (common in imported records) starts on line 1, so saving
// it does not drop the only copy of the address.
export function addressToFields(address: Address | undefined): AddressFields {
  const line = address?.line?.length ? address.line : [address?.text ?? '']
  return {
    line1: line[0] ?? '',
    line2: line.slice(1).filter(Boolean).join(', '),
    city: address?.city ?? '',
    state: address?.state ?? '',
    postalCode: address?.postalCode ?? '',
  }
}

// Keeps what the fields don't edit (use, type, period, extensions) from
// `base`. `text` is dropped: it would no longer match the edited lines.
export function applyAddressFields(
  base: Address | undefined,
  fields: AddressFields
): Address {
  const { text: _staleText, ...rest } = base ?? {}
  const line = [fields.line1.trim(), fields.line2.trim()].filter(Boolean)
  return {
    ...rest,
    line: line.length > 0 ? line : undefined,
    city: fields.city.trim() || undefined,
    state: fields.state.trim().toUpperCase() || undefined,
    postalCode: fields.postalCode.trim() || undefined,
    country: rest.country ?? 'US',
  }
}

function comparableFields(fields: AddressFields): AddressFields {
  return mapValues(fields, (value) => value.trim().toUpperCase())
}

// A suggestion identical to what is in the fields offers nothing to accept:
// a `corrected` result then reads as verified, and any other result drops the
// suggested address.
export function withoutRedundantSuggestion(
  result: AddressValidationResult,
  fields: AddressFields
): AddressValidationResult {
  if (
    !result.standardized ||
    !isEqual(
      comparableFields(addressToFields(result.standardized)),
      comparableFields(fields)
    )
  ) {
    return result
  }
  return result.status === 'corrected'
    ? { ...result, status: 'valid' }
    : { ...result, standardized: undefined }
}

const COMPONENT_LABELS = new Map([
  ['street_number', 'street number'],
  ['route', 'street'],
  ['subpremise', 'unit'],
  ['premise', 'building'],
  ['locality', 'city'],
  ['postal_town', 'city'],
  ['administrative_area_level_1', 'state'],
  ['postal_code', 'ZIP code'],
  ['country', 'country'],
])

// Google component types as words a scheduler reads, deduplicated, in order.
export function describeAddressComponents(types: readonly string[]): string[] {
  return [
    ...new Set(
      types.map(
        (type) => COMPONENT_LABELS.get(type) ?? type.replaceAll('_', ' ')
      )
    ),
  ]
}
